<template>
  <Drawer
    v-model:visible="isVisible"
    :header="drawerHeader"
    position="right"
    class="!w-full md:!w-[500px]"
    :modal="true"
    block-scroll
  >
    <!-- Step 1: choose relationship type -->
    <div v-if="step === 'type'" class="flex flex-col gap-3">
      <p class="text-sm text-gray-600">What kind of relationship is this?</p>
      <button
        type="button"
        class="border rounded-md p-4 text-left hover:border-primary"
        data-testid="choose-parent"
        @click="chooseType('parent')"
      >
        <div class="text-sm font-medium">Parent</div>
        <div class="text-xs text-gray-500">
          Full access to view, manage, and pay
        </div>
      </button>
      <button
        type="button"
        class="border rounded-md p-4 text-left hover:border-primary"
        data-testid="choose-payer"
        @click="chooseType('payer')"
      >
        <div class="text-sm font-medium">Other contact</div>
        <div class="text-xs text-gray-500">
          Can pay, but has no other access to this player
        </div>
      </button>
    </div>

    <!-- Step 2: pick or create the person -->
    <div v-else class="flex flex-col gap-4">
      <Tabs v-model:value="activeTab">
        <TabList>
          <Tab value="existing" class="text-sm font-bold">Existing user</Tab>
          <Tab value="new" class="text-sm font-bold">New user</Tab>
        </TabList>
        <TabPanels>
          <!-- Existing user -->
          <TabPanel value="existing">
            <div class="flex flex-col gap-3 pt-2">
              <Select
                id="existing-user"
                v-model="selectedProfileId"
                :options="profileOptions"
                option-label="label"
                option-value="value"
                :placeholder="
                  direction === 'add-contact'
                    ? 'Search for a user'
                    : 'Search for a player'
                "
                class="w-full"
                filter
                append-to="body"
                :loading="isLoadingProfiles"
              />
              <Button
                label="Add relationship"
                class="mt-2"
                :loading="isSubmitting"
                :disabled="!selectedProfileId"
                data-testid="submit-existing"
                @click="submitExisting"
              />
            </div>
          </TabPanel>

          <!-- New user -->
          <TabPanel value="new">
            <div class="flex flex-col gap-3 pt-2">
              <div class="space-y-1">
                <label for="rel-name" class="text-sm font-medium text-gray-700">
                  Name <span class="text-red-500">*</span>
                </label>
                <InputText
                  id="rel-name"
                  v-model="newUserForm.name"
                  class="w-full"
                  :invalid="v$.name.$error"
                />
                <small v-if="v$.name.$error" class="text-xs text-red-500">
                  Name is required
                </small>
              </div>

              <div class="space-y-1">
                <label
                  for="rel-email"
                  class="text-sm font-medium text-gray-700"
                >
                  Email
                  <span v-if="!newUserForm.noEmail" class="text-red-500"
                    >*</span
                  >
                </label>
                <InputText
                  id="rel-email"
                  v-model="newUserForm.email"
                  class="w-full"
                  :disabled="newUserForm.noEmail"
                  :invalid="v$.email.$error"
                />
                <small v-if="v$.email.$error" class="text-xs text-red-500">
                  A valid email is required
                </small>
              </div>

              <!-- No-email managed athlete: only when creating a player under player_model -->
              <div
                v-if="direction === 'add-player' && playerModelEnabled"
                class="flex items-center gap-2"
              >
                <Checkbox
                  v-model="newUserForm.noEmail"
                  :binary="true"
                  input-id="rel-no-email"
                />
                <label for="rel-no-email" class="text-sm text-gray-700">
                  This athlete doesn't have their own email
                </label>
              </div>

              <div class="space-y-1">
                <label
                  for="rel-phone"
                  class="text-sm font-medium text-gray-700"
                >
                  Phone
                </label>
                <InputText
                  id="rel-phone"
                  v-model="newUserForm.phone_number"
                  class="w-full"
                  placeholder="+1 (555) 555-5555"
                />
              </div>

              <div v-if="direction === 'add-player'" class="space-y-1">
                <label for="rel-team" class="text-sm font-medium text-gray-700">
                  Team
                </label>
                <Select
                  id="rel-team"
                  v-model="newUserForm.team_id"
                  :options="teams"
                  option-label="name"
                  option-value="id"
                  placeholder="Select a team"
                  class="w-full"
                  show-clear
                  append-to="body"
                />
              </div>

              <Button
                label="Create & add relationship"
                class="mt-2"
                :loading="isSubmitting"
                data-testid="submit-new"
                @click="submitNew"
              />
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import Select from "primevue/select";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import { useToast } from "primevue/usetoast";
import { useVuelidate } from "@vuelidate/core";
import { required, email as emailRule } from "@vuelidate/validators";
import { useWaffle } from "@/shared/composables/useWaffle";
import { useCreateUser } from "../composables/useCreateUser";
import { useOrganizationProfiles } from "../composables/useOrganizationProfiles";
import { useOrganizationTeams } from "../composables/useOrganizationTeams";
import { addContactToUser, linkPlayerToContact } from "../api";
import { ProfileRole } from "../constants/roles";
import type { AdminProfile } from "@/api/model";

interface Props {
  visible?: boolean;
  /** "add-contact": add a parent/payer to a player. "add-player": add a player to a contact. */
  direction: "add-contact" | "add-player";
  user: AdminProfile;
  organizationId: number;
}

const props = withDefaults(defineProps<Props>(), { visible: false });
const emit = defineEmits<{
  "update:visible": [value: boolean];
  "relationship-added": [];
}>();

const toast = useToast();
const waffle = useWaffle();
const playerModelEnabled = computed(() => waffle.isActive("player_model"));

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

// Step 1: relationship type — player side only. A parent adding a player
// ("Add Player", per the design) skips the chooser: the link takes the
// Parent default. "Other contact" sends is_parent: false explicitly —
// omitting it would default to true and show a Parent badge.
const step = ref<"type" | "form">(
  props.direction === "add-player" ? "form" : "type"
);
const relType = ref<"parent" | "payer">("parent");

function chooseType(type: "parent" | "payer") {
  relType.value = type;
  step.value = "form";
}

const relationshipFlags = computed(() => {
  if (props.direction === "add-player") return undefined; // defaults: both
  return relType.value === "parent"
    ? { is_parent: true }
    : { is_parent: false };
});

const drawerHeader = computed(() => {
  if (props.direction === "add-player") return "Add Player";
  if (step.value === "type") return "Add Relationship";
  return relType.value === "parent"
    ? "Add parent relationship"
    : "Add “other contact” relationship";
});

// Step 2 state
const activeTab = ref("existing");
const isSubmitting = ref(false);

// Existing-user options
const orgIdRef = computed(() => props.organizationId);
const { isLoading: isLoadingProfiles, fetchProfiles } =
  useOrganizationProfiles(orgIdRef);
const profileOptions = ref<{ label: string; value: number; email?: string }[]>(
  []
);

watch(
  step,
  async (s) => {
    if (s !== "form" || profileOptions.value.length > 0) return;
    waffle.fetchStatus();
    try {
      const profiles = await fetchProfiles(
        props.direction === "add-player"
          ? { role: ProfileRole.player, excludeIds: [props.user.id] }
          : { excludeIds: [props.user.id] }
      );
      profileOptions.value = profiles
        // adding a contact requires a login email on the chosen person
        .filter((p) => props.direction === "add-player" || p.email)
        .map((p) => ({
          label: p.email ? `${p.name} (${p.email})` : p.name,
          value: p.id,
          email: p.email ?? undefined,
        }));
    } catch (e) {
      console.error("Failed to load users:", e);
    }
  },
  // add-player opens directly on the form step, so run immediately too
  { immediate: true }
);

const selectedProfileId = ref<number | null>(null);

// New-user form
const newUserForm = reactive({
  name: "",
  email: "",
  noEmail: false,
  phone_number: "",
  team_id: null as number | null,
});

const rules = computed(() => ({
  name: { required },
  email: newUserForm.noEmail ? {} : { required, email: emailRule },
}));
const v$ = useVuelidate(rules, newUserForm);

const { teams, loadTeams } = useOrganizationTeams(props.organizationId);
watch(
  step,
  (s) => {
    if (s === "form" && props.direction === "add-player") loadTeams();
  },
  // add-player opens directly on the form step, so run immediately too
  { immediate: true }
);

const { createUser } = useCreateUser();

const contactId = computed<number | null>(
  () => props.user.associated_contact_id ?? null
);

// A user with no parent/payer record yet (e.g. roleless, MAI-4998) becomes one
// by adding their first player: addContactToUser creates the contact from
// their email and links the player in one call (409 self-heals into add_profile).
function canBootstrapContact(): boolean {
  if (contactId.value == null && !props.user.email) {
    toastError({
      message:
        "This user has no email, so they can't be made a parent or payer.",
    });
    return false;
  }
  return true;
}

async function linkPlayerAsThisUser(playerProfileId: number) {
  if (contactId.value != null) {
    await linkPlayerToContact(
      props.organizationId,
      contactId.value,
      playerProfileId,
      relationshipFlags.value
    );
    return;
  }
  const result = await addContactToUser(playerProfileId, {
    name: props.user.name || props.user.email || "",
    email: props.user.email || "",
    organization: props.organizationId,
    profile: playerProfileId,
    primary_contact: false,
    ...relationshipFlags.value,
  });
  if (result && "error" in result) throw result;
}

async function submitExisting() {
  if (!selectedProfileId.value) return;
  isSubmitting.value = true;
  try {
    if (props.direction === "add-player") {
      if (!canBootstrapContact()) return;
      await linkPlayerAsThisUser(selectedProfileId.value);
    } else {
      // link an existing user as a contact of this player (creates the
      // contact from their email; 409 self-heals into add_profile)
      const option = profileOptions.value.find(
        (o) => o.value === selectedProfileId.value
      );
      const result = await addContactToUser(props.user.id, {
        name: option?.label.replace(/ \(.*\)$/, "") || "",
        email: option?.email || "",
        organization: props.organizationId,
        profile: props.user.id,
        primary_contact: false,
        ...relationshipFlags.value,
      });
      if (result && "error" in result) throw result;
    }
    onAdded();
  } catch (e: any) {
    toastError(e);
  } finally {
    isSubmitting.value = false;
  }
}

async function submitNew() {
  const valid = await v$.value.$validate();
  if (!valid) return;

  isSubmitting.value = true;
  try {
    if (props.direction === "add-player") {
      // Create the player via the shared create path. When this user already
      // has a contact record, a no-email athlete gets the guardian linked
      // atomically by the backend; otherwise the link (which also mints the
      // contact record from this user's email) happens right after create.
      if (!canBootstrapContact()) return;
      const cid = contactId.value;
      const created = await createUser(
        {
          name: newUserForm.name,
          email: newUserForm.noEmail ? undefined : newUserForm.email,
          phone_number: newUserForm.phone_number || undefined,
          organization_id: props.organizationId,
          // Player-model players need the role sent explicitly (MAI-5020) —
          // without it a teamless athlete is created role-less and invisible
          // to the portal's participant matching. Mirrors UserCreateDrawer.
          roles: playerModelEnabled.value
            ? (["player"] as ProfileRole[])
            : undefined,
          guardian_contact_id:
            newUserForm.noEmail && cid != null ? cid : undefined,
        },
        newUserForm.team_id ? [newUserForm.team_id] : undefined
      );
      if (!created) return; // createUser already toasted the real error

      // The backend links no-email players itself when a guardian id was sent;
      // every other combination still needs the link made here.
      if (!(newUserForm.noEmail && cid != null)) {
        await linkPlayerAsThisUser(created.id);
      }
    } else {
      const result = await addContactToUser(props.user.id, {
        name: newUserForm.name,
        email: newUserForm.email,
        phone_number: newUserForm.phone_number || undefined,
        organization: props.organizationId,
        profile: props.user.id,
        primary_contact: false,
        ...relationshipFlags.value,
      });
      if (result && "error" in result) throw result;
    }
    onAdded();
  } catch (e: any) {
    toastError(e);
  } finally {
    isSubmitting.value = false;
  }
}

function onAdded() {
  toast.add({
    severity: "success",
    summary: "Relationship added",
    life: 3000,
  });
  emit("relationship-added");
  isVisible.value = false;
}

function toastError(e: any) {
  const data = e?.data || e?.response?.data;
  const detail =
    (typeof data === "string" && data) ||
    data?.detail ||
    (Array.isArray(data?.email) ? `Email: ${data.email[0]}` : data?.email) ||
    data?.guardian_contact_id ||
    data?.error ||
    e?.error ||
    e?.message ||
    "Failed to add relationship";
  toast.add({ severity: "error", summary: "Error", detail, life: 6000 });
}
</script>
